import * as $http from '../helpers/jquery.helper';
import { UserBookingDetailsModel } from '../models/user-booking-details.model';
import { Error } from '../models/error';
import { CheckoutRequestModel } from '../models/checkout-request.model';
import { EnvModel } from '../models/env.model';
import { CartModel } from '../models/cart.model';
import { TermsAndConditionsModel } from '../models/terms-and-conditions.model';
import { TrackingCode } from '../models/tracking-code.model';
import { CheckoutPayload } from '../models/checkout-payload.model';
import { PaymentGatewayGroupModel } from '../models/payment-group.model';

export default class DataService {
    public GetBrowserTimezone(): number {
        const browserDate = new Date();
        const browserTimezoneOffset = browserDate.getTimezoneOffset();
        const browserTimezone = (browserTimezoneOffset * -1) / 60;
        return browserTimezone;
    }

    constructor(private env: EnvModel) {
    }

    public GetShoppingCart() {
        return $http.GetJsonWithCred(`${this.env.shoppingCartApiPath}/shoppingcart`, false);
    }
    
    public GetPaymentGroup(accountId: number): JQuery.jqXHR<PaymentGatewayGroupModel> {
        return $.get(`${this.env.checkoutSitePath}/event/shoppingcart/paymentGateway?accountId=${accountId}`);
    };

    public GetSurcharges(accountId: number) {
        return $.get(`${this.env.checkoutSitePath}/event/checkout/surcharges/${accountId}`);
    }

    public GetEventTermsAndCondition(eventId: number) {
        return $.get(`${this.env.eventSitePath}/terms/${eventId}/termsAndConditions`);
    }

    public GetFatZebraToken(paymentGatewayId: number): JQuery.jqXHR<string> {
        return $.get(`${this.env.checkoutSitePath}/event/checkout/GenerateFatZebraToken?paymentGatewayId=${paymentGatewayId}`);
    }
    public RetrieveFatZebraTokenizedCard(token: string, paymentGatewayId: number) {
        return $.get(`${this.env.checkoutSitePath}/event/checkout/RetrieveFatZebraTokenizedCard?token=${token}&paymentGatewayId=${paymentGatewayId}`);
    }
    public CalculateVerification(value: string, paymentGatewayId: number) {
        return $.get(`${this.env.checkoutSitePath}/event/checkout/CalculateVerification?value=${value}&paymentGatewayId=${paymentGatewayId}`);
    }
    
    public GetBrainTreeToken(paymentGatewayId: number) {
        return $.get(`${this.env.checkoutSitePath}/event/checkout/braintreeToken?paymentGatewayId=${paymentGatewayId}`);
    }
    public GetAccountHasOtherEvents(aid: number, eid: number) {
        return $.get(`${this.env.checkoutSitePath}/event/checkout/accountEvents?aid=${aid}&eid=${eid}`);
    }
    public GetAccountHasGcs(aid: number) {
        return $.get(`${this.env.checkoutSitePath}/event/checkout/accountGcs?aid=${aid}`);
    }
    public GetFatZebraApplePaySession(paymentGatewayId: number, validationUrl: string) {
        return $.get(`${this.env.checkoutSitePath}/event/checkout/FatZebraApplePaySession?paymentGatewayId=${paymentGatewayId}&validationUrl=${validationUrl}`);
    }

    public GetEventsTAndC(eventIds: number[]) {
        const eventTerms: any[] = [];
        const deferred = $.Deferred();

        // TO DO: consider using es6 iterator
        $.each(eventIds, (index, value) => {
            this.GetEventTermsAndCondition(value)
                .then((data: TermsAndConditionsModel) => { // consider using arrow function
                    eventTerms.push(data);
                    if (eventTerms.length === eventIds.length) {
                        const sortedTerms: any[] = [];

                        $.each(eventIds, function (j, v) {
                            var tIndex = eventTerms.findIndex((terms: TermsAndConditionsModel) => {
                                // TODO: update casing of Event object and its properties when legacy api is fully decommissioned
                                return terms.eventId === v;
                            });

                            sortedTerms.push(eventTerms[tIndex]);
                        });
                        deferred.resolve(sortedTerms);
                    }
                }, function () {
                    deferred.reject();
                });
        });

        return deferred.promise();

    }

    public ProcessBooking(shoppingCart: CartModel, userDetails: UserBookingDetailsModel, payload: CheckoutPayload) {
        if (!shoppingCart) {
            console.error('shopping cart object is missing...');
            return;
        }
        const urlParams = new URLSearchParams(window.location.search);

        const checkoutViewModelRequest: CheckoutRequestModel = {
            ...payload,
            ...payload?.creditCard,

            source: urlParams.get('source') ?? undefined,
            UserDetails: userDetails,
            bookingEventDataCollection: shoppingCart.bookingEventDataCollection,
            boxOfficeFees: shoppingCart.boxOfficeFees?.map(x => MapBoxOfficeFee(x)),
            donations: shoppingCart.donations?.map(x => MapDonation(x)),
            seats: shoppingCart.seats?.map(x => MapSeat(x)),
            tickets: shoppingCart.tickets?.map(x => MapTicket(x)),
            giftCertificates: shoppingCart.giftCertificates?.map(x => MapGiftCertificate(x)),
            ticketDataCollection: shoppingCart.ticketDataCollection?.map(x => MapTicketDataCollection(x)),
            promotionCodes: shoppingCart.promotionCodeDetails?.map(x => MapAppliedPromo(x)),
        };
        
        return $http.postJSON(`${this.env.checkoutSitePath}/event/checkout/processpayment`, checkoutViewModelRequest, true);
    
        function MapAppliedPromo(model: any) {
            return {
                Code: model.code,
                DiscountAmount: model.discountAmount,
                IsPromoGC: model.isPromoGC,
                Type: model.promotionCodeType
            };
        }

        function MapApplieDiscount(model: any) {
            return {
                Code: model.code,
                Value: model.amount
            };
        }

        function MapAppliedDiscounts(modelList: any[]) {
            if (modelList) {
                const bookingDcs: any[] = [];

                $.each(modelList, (index: number, value: any) => {
                    bookingDcs.push(MapApplieDiscount(value));
                });

                return bookingDcs;
            }

            return undefined;
        }

        function MapAppliedGc(model: any) {
            return {
                AppliedValue: model.amount,
                AppliedValueOnFee: model.feeAmount,
                Code: model.code
            };
        }

        function MapAppliedGcs(modelList: any[]) {
            if (modelList) {
                const appliedGcs: any[] = [];

                $.each(modelList, (index: number, value: any) => {
                    appliedGcs.push(MapAppliedGc(value));
                });

                return appliedGcs;
            }
            return undefined;
        }

        function MapTicket(model: any) {
            return {
                AccountId: model.accountId,
                EventId: model.eventId,
                SessionTimeId: model.eventDate.id,
                SectionId: model.section.id,
                Quantity: model.quantity,
                AppliedDiscounts: MapAppliedDiscounts(model.bookingDiscounts),
                AppliedGiftCertificates: MapAppliedGcs(model.appliedGiftCertificates),
                AppliedGCOnUnitPrice: model.appliedGCOnUnitPrice,
                AppliedGCOnTryBookingFee: model.appliedGCOnTryBookingFee,
                BookingFee: model.bookingFeeSurcharge,
                TicketType: { Id: model.ticketType.id, Name: model.ticketType.name }
            };
        }

        function MapGiftCertificate(model: any) {
            return {
                AccountId: model.accountId,
                GiftCertificateId: model.id,
                TypeId: model.giftCertificateTypeId,
                To: model.to,
                From: model.from,
                PromoCodes: model.bookingDiscounts,
            };
        }

        function MapSeat(seatModel: any) {
            return {
                SessionTimeId: seatModel.sessionTimeId,
                RowName: seatModel.rowName,
                SeatNumber: parseInt(seatModel.seatNumber),
                TicketPriceId: seatModel.ticketPriceId,
                SectionId: seatModel.sectionId
            };
        }

        function MapDonation(model: any) {
            return {
                EventId: model.eventId,
                SessionTimeId: model.sessionTimeId,
                DonatedAmount: model.donatedAmount,
                AppliedGC: MapAppliedGcs(model.appliedGiftCertificates),
                AppliedGCAmount: model.appliedGCAmount
            };
        }
        
        function MapBoxOfficeFee(model: any) {
            return {
                EventId: model.eventId,
                BoxOfficeFeeAmount: model.boxOfficeFeeAmount,
                AppliedGCAmount: model.appliedGCAmount
            };
        }

        function MapTicketDataCollection(bTicket: any) {
            return {
                EventId: bTicket.eventId,
                EventDateId: bTicket.eventDateId,
                SectionId: bTicket.sectionId,
                TicketTypeId: bTicket.ticketTypeId,
                RowName: bTicket.rowName,
                SeatNumber: parseInt(bTicket.seatNumber),
                DataFieldId: bTicket.dataFieldId,
                Value: bTicket.value,
                BookingAttendeeTempId: bTicket.bookingAttendeeTempId,
                OptionFieldId: bTicket.optionFieldId
            };
        }
    }


    public LogJavascriptWarning(error: Error) {
        return $http.postJSON(`${this.env.checkoutSitePath}/event/checkout/logjavascriptwarning`, error, true);
    }

    public ClearShoppingCart() {
        return $http.GetJsonWithCred(`${this.env.shoppingCartApiPath}/shoppingcart/clear`);
    }

    public ApplyGcOrPromoCode(codes: any) {
        return $http.postJSON(`${this.env.shoppingCartApiPath}/shoppingcart/promotion`, codes, true);
    }

    public ApplyBoxOfficeFee(bofModel: any) {
        return $http.postJSON(`${this.env.shoppingCartApiPath}/shoppingcart/quicksale-fee`, bofModel, true);
    }

    public CheckHasValidPromoCodes(model: any) {
        return $http.postJSON(`${this.env.checkoutSitePath}/event/checkout/hasValidPromoCodes`, model, true);
    }

    public RemoveTicketItem(cartItem: any) {
        return $http.postJSON(`${this.env.shoppingCartApiPath}/shoppingcart/tickets/delete`, cartItem, true);
    }

    public RemoveDonationItem(cartItem: any) {
        return $http.postJSON(`${this.env.shoppingCartApiPath}/shoppingcart/donations/delete`, cartItem, true);
    }

    public RemoveGCBookingItem(gcBooking: any) {
        return $http.postJSON(`${this.env.shoppingCartApiPath}/shoppingcart/delete-gc-reservation`, gcBooking);
    }

    public GetTrackingCodes(eventIds: number[]): JQuery.jqXHR<TrackingCode> {
        return $http.postJSON(`${this.env.checkoutSitePath}/event/checkout/trackingcodes`, eventIds);
    }

    public ValidateQuickSaleId(accountId: number, quickSaleId: string) {
        return $.getJSON(`${this.env.checkoutSitePath}/event/checkout/validateQuickSale?accountId=${accountId}&qs=${quickSaleId}`);
    }

    public GetPayPalOrderDetails(orderId: string) {
        return $.get(`${this.env.checkoutSitePath}/event/checkout/order/${orderId}`);
    }

    public ContactEO(model: any) {
        return $http.postJSON(`${this.env.eventSitePath}/contact-event-organiser`, model, true);
    }

    public GetEventOrganiserEvents(aid: number) {
        return $.get(`${this.env.eventSitePath}/api/v1/search?accountId=${aid}`);
    }

    public GetAccountName(aid: number) {
        return $.get(`${this.env.checkoutSitePath}/event/checkout/get-account-name?aid=${aid}`);
    }

    public GetAccountRecaptchaSettings(aid: number) {
        return $.get(`${this.env.checkoutSitePath}/event/checkout/get-account-recaptcha-settings?aid=${aid}`);
    }

    public GetCorrection(domain: string) {
        return $.get(`${this.env.checkoutSitePath}/event/checkout/email-correction?domain=${domain}`);
    }
}
